import { Typography, Box } from "@mui/material";
import { ArrowRight } from "@mui/icons-material";
import { NodeModel, RenderParams } from "@minoru/react-dnd-treeview";
import { useCallback, useEffect, useRef } from "react";
import { FileProperties } from "../models/FileProperties";
import { TypeIcon } from "./DelayConfiguration/TypeIcon";

type Props = RenderParams & {
  node: NodeModel<FileProperties>;
  setContextMenuOver: (
    data: NodeModel<FileProperties>,
    e: React.MouseEvent<HTMLElement>,
  ) => void;
  onDoubleClick: (
    data: NodeModel<FileProperties>,
    e: React.MouseEvent<HTMLElement>,
  ) => void;
  isSelected: boolean;
  isDragging: boolean;
  testIdPrefix?: string;
  onClick: (e: React.MouseEvent, node: NodeModel<FileProperties>) => void;
  "data-node-id"?: string;
  openDelays: Record<number | string, number | string>;
  setOpenDelays: (delays: Record<number | string, number | string>) => void;
};

export const CustomNode: React.FC<Props> = ({
  setContextMenuOver,
  onDoubleClick,
  testIdPrefix = "",
  isSelected,
  openDelays,
  setOpenDelays,
  ...props
}) => {
  const { id, droppable, data } = props.node;
  const indent = props.depth * 24;

  const handleClick = (e: React.MouseEvent) => {
    props.onClick(e, props.node);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (openDelays[id]) {
      const newOpenAssets = { ...openDelays };
      delete newOpenAssets[id as number];
      setOpenDelays(newOpenAssets);
    } else {
      setOpenDelays({ ...openDelays, [id]: id });
    }
    props.onToggle();
  };

  const nodeRef = useRef(props.node);
  const setContextMenuOverRef = useRef(setContextMenuOver);
  const onDoubleClickRef = useRef(onDoubleClick);

  useEffect(() => {
    nodeRef.current = props.node;
    setContextMenuOverRef.current = setContextMenuOver;
    onDoubleClickRef.current = onDoubleClick;
  });

  const handleContextMenu = useCallback((ev: MouseEvent) => {
    ev.stopPropagation();
    ev.preventDefault();
    setContextMenuOverRef.current(
      nodeRef.current,
      ev as unknown as React.MouseEvent<HTMLElement>,
    );
  }, []);

  const handleViewAssetDialog = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDoubleClickRef.current(
      nodeRef.current,
      e as unknown as React.MouseEvent<HTMLElement>,
    );
    setContextMenuOverRef.current(
      {
        id: "",
        parent: "",
        text: "",
        droppable: false,
      },
      e as unknown as React.MouseEvent<HTMLElement>,
    );
  }, []);

  useEffect(() => {
    props.containerRef.current?.addEventListener(
      "contextmenu",
      handleContextMenu,
    );
    props.containerRef.current?.addEventListener(
      "dblclick",
      handleViewAssetDialog,
    );

    return () => {
      props.containerRef.current?.removeEventListener(
        "contextmenu",
        handleContextMenu,
      );
      props.containerRef.current?.removeEventListener(
        "dblclick",
        handleViewAssetDialog,
      );
    };
  }, []);

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        height: "32px",
        listStyleType: "none",
        margin: 0,
        padding: 0,
        paddingInlineStart: `${indent}px`,
        backgroundColor: isSelected ? "#e8f0fe" : "transparent",
      }}
      data-node-id={id}
      onClick={handleClick}
      title={`${props.node.id} - ${props.node.text}`}
    >
      {props.node.droppable ? (
        <Box
          sx={{
            alignItems: "center",
            cursor: "pointer",
            display: "flex",
            height: "24px",
            justifyContent: "center",
            width: "24px",
            transition: "transform linear 0.1s",
            transform: props.isOpen ? "rotate(90deg)" : "rotate(0deg)",
            "& > div": {
              alignItems: "center",
              display: "flex",
            },
          }}
        >
          <Box onClick={handleToggle}>
            <ArrowRight data-testid={`arrow-right-icon-${id}`} />
          </Box>
        </Box>
      ) : null}
      <Box sx={{ display: "flex" }}>
        <TypeIcon droppable={droppable as boolean} fileType={data?.fileType} />
      </Box>
      <Box sx={{ paddingInlineStart: "8px" }}>
        <Typography variant="body2">{props.node.text}</Typography>
      </Box>
    </Box>
  );
};
